// Language auto-detect. The wire value is the cross-client contract: the
// agent (opalite-livekit) normalizes 'auto' (case-insensitive) to its
// unknown-language sentinel, runs Soniox language identification with no
// hints, and locks the detected language in via the
// `session_language_detected` RPC. See MESSAGE_STRUCTURES.md in
// opalite-livekit.
export const AUTO_DETECT_WIRE_VALUE = 'auto';
export const AUTO_DETECT_LABEL = 'Auto-detect';

export const isAutoDetectLanguage = (value: unknown): boolean =>
  String(value || '')
    .trim()
    .toLowerCase() === AUTO_DETECT_WIRE_VALUE;

// UI display name for a session language value ('auto' shows as its label).
export const displayLanguageName = (value: unknown): string => {
  const name = String(value || '').trim();
  return isAutoDetectLanguage(name) ? AUTO_DETECT_LABEL : name;
};

// Language variants and dialects for supported languages
// Ordered by: Common languages first, then alphabetical
export const SUPPORTED_LANGUAGES: Record<string, string[]> = {
  // === COMMON LANGUAGES (in order of frequency for US-based translation) ===
  Spanish: [
    'Mexican Spanish',
    'European Spanish (Castilian)',
    'Argentinian Spanish',
    'Caribbean Spanish',
    'Puerto Rican Spanish',
    'Dominican Spanish',
    'Colombian Spanish',
    'Guatemalan Spanish',
  ],
  Chinese: [
    'Mandarin (Simplified)',
    'Mandarin (Traditional)',
    'Cantonese',
    'Hokkien (Min Nan)',
  ],
  Russian: ['Standard Russian', 'Southern Russian'],
  French: ['Metropolitan French', 'Canadian French (Quebec)', 'African French'],
  Korean: ['Standard Korean', 'Seoul Dialect', 'Jeju Dialect'],
  German: ['Standard German', 'Swiss German', 'Austrian German'],
  Italian: ['Standard Italian', 'Sicilian', 'Northern Italian'],
  Portuguese: [
    'Brazilian Portuguese',
    'European Portuguese',
    'African Portuguese',
  ],
  Arabic: [
    'Egyptian Arabic',
    'Levantine Arabic',
    'Gulf Arabic',
    'Yemeni Arabic',
    'Modern Standard Arabic',
  ],
  Hindi: ['Standard Hindi', 'Awadhi', 'Braj Bhasha'],
  Bengali: ['Standard Bengali', 'Sylheti', 'Chittagonian'],
  Vietnamese: [
    'Northern Vietnamese',
    'Central Vietnamese',
    'Southern Vietnamese',
  ],
  Turkish: ['Standard Turkish', 'Cypriot Turkish'],
  Thai: ['Central Thai', 'Northern Thai', 'Southern Thai'],
  Japanese: ['Standard Japanese', 'Kansai', 'Okinawan'],
  Indonesian: ['Standard Indonesian', 'Jakarta Indonesian'],
  Armenian: ['Eastern Armenian', 'Western Armenian'],
  Persian: [
    'Iranian Persian (Farsi)',
    'Dari (Afghan Persian)',
    'Tajik Persian',
  ],
  Tagalog: ['Manila Tagalog', 'Cebuano Tagalog'],
  Polish: [],
  Dutch: [],
  Swedish: [],
  Romanian: [],
  Czech: [],
  Hungarian: [],
  Finnish: [],

  // === ALPHABETICAL LANGUAGES ===
  Ukrainian: [],
  'Cape Verdean Creole': [],
  'Haitian Creole': [],
  Danish: [],
  Greek: [],
  Norwegian: ['Bokmål', 'Nynorsk'],
  Bulgarian: [],
  Slovak: [],
  Lithuanian: [],
  Catalan: [],
  Slovenian: [],
  Swahili: ['Tanzanian Swahili', 'Kenyan Swahili', 'Congo Swahili'],
  Hebrew: ['Modern Hebrew', 'Sephardic Hebrew'],
  Estonian: [],
  Latvian: [],
  Albanian: [],
  Malay: [],
  Azerbaijani: [],
  Serbian: [],
  Tamil: [],
  Croatian: [],
  Kazakh: [],
  Malayalam: [],
  Marathi: [],
  Telugu: [],
  Afrikaans: [],
  Galician: [],
  Belarusian: [],
  Macedonian: [],
  Basque: [],
  Bosnian: [],
  Uzbek: [],
  Urdu: [],
  Nepali: [],
  Kannada: [],
  Gujarati: [],
  Kyrgyz: [],
  Tajik: [],
  Burmese: [],
  Tatar: [],
  Somali: ['Standard Somali', 'Maay Somali'],
  Amharic: [],
  Yiddish: [
    'Eastern Yiddish (Litvish)',
    'Central Yiddish (Poylish)',
    'Western Yiddish',
  ],
  Zulu: [],
  Odia: [],
  Luganda: [],
  'Kurdish (Sorani)': [],
  Kabyle: [],
};

// Most common languages for US-based translation needs
export const COMMON_LANGUAGES = [
  'Spanish',
  'Chinese',
  'Vietnamese',
  'Korean',
  'Arabic',
  'French',
  'Russian',
  'German',
  'Japanese',
  'Portuguese',
  'Italian',
  'Hindi',
  'Bengali',
  'Urdu',
  'Indonesian',
  'Turkish',
  'Thai',
  'Polish',
  'Ukrainian',
  'Dutch',
  'Greek',
  'Czech',
  'Romanian',
  'Hungarian',
  'Somali',
  'Yiddish',
];

// Most common dialect per language for US-based translation needs — tagged
// "Common" in dialect pickers. Kept in lockstep with the iOS / Android
// ViewModel commonDialects maps.
export const COMMON_DIALECTS: Record<string, string> = {
  Spanish: 'Mexican Spanish',
  Chinese: 'Mandarin (Simplified)',
  Arabic: 'Egyptian Arabic',
  French: 'Metropolitan French',
  Portuguese: 'Brazilian Portuguese',
  German: 'Standard German',
  Italian: 'Standard Italian',
  Russian: 'Standard Russian',
  Korean: 'Standard Korean',
  Vietnamese: 'Southern Vietnamese',
  Hindi: 'Standard Hindi',
  Bengali: 'Standard Bengali',
  Japanese: 'Standard Japanese',
  Turkish: 'Standard Turkish',
  Thai: 'Central Thai',
  Persian: 'Iranian Persian (Farsi)',
  Armenian: 'Eastern Armenian',
  Tagalog: 'Manila Tagalog',
  Norwegian: 'Bokmål',
  Swahili: 'Tanzanian Swahili',
  Hebrew: 'Modern Hebrew',
  Yiddish: 'Eastern Yiddish (Litvish)',
  Indonesian: 'Standard Indonesian',
  Somali: 'Standard Somali',
};

// Dialects/variants offered for a language when translating text or
// documents. Empty when the language has no distinct variants worth
// exposing.
export const dialectsForLanguage = (language: string): string[] =>
  SUPPORTED_LANGUAGES[language] || [];

// Human-readable target passed to the translation backend. The document and
// text pipelines both take a plain language name (optionally dialect-
// qualified) that the model translates into — e.g. "Chinese (Cantonese)" or
// "Mexican Spanish". Keep this format identical across surfaces so the same
// string reaches the backend regardless of which translator produced it.
export const formatLanguageTarget = (
  language: string,
  dialect?: string,
): string => (dialect ? `${language} (${dialect})` : language);

export const TELEPHONIC_SUPPORTED_LANGUAGES: (
  | string
  | Record<string, string[]>
)[] = [
  {
    Spanish: [
      'Mexican Spanish',
      'European Spanish (Castilian)',
      'Argentinian Spanish',
      'Caribbean Spanish',
      'Puerto Rican Spanish',
      'Dominican Spanish',
      'Colombian Spanish',
      'Guatemalan Spanish',
    ],
  },
  {
    Chinese: [
      'Mandarin (Simplified)',
      'Mandarin (Traditional)',
      'Cantonese',
      'Hokkien (Min Nan)',
    ],
  },
  {Russian: ['Standard Russian', 'Southern Russian']},
  {
    French: [
      'Metropolitan French',
      'Canadian French (Quebec)',
      'African French',
    ],
  },
  {Korean: ['Standard Korean', 'Seoul Dialect', 'Jeju Dialect']},
  {German: ['Standard German', 'Swiss German', 'Austrian German']},
  {Italian: ['Standard Italian', 'Sicilian', 'Northern Italian']},
  {
    Portuguese: [
      'Brazilian Portuguese',
      'European Portuguese',
      'African Portuguese',
    ],
  },
  {
    Arabic: [
      'Egyptian Arabic',
      'Levantine Arabic',
      'Gulf Arabic',
      'Yemeni Arabic',
      'Modern Standard Arabic',
    ],
  },
  {Hindi: ['Standard Hindi', 'Awadhi', 'Braj Bhasha']},
  {Bengali: ['Standard Bengali', 'Sylheti', 'Chittagonian']},
  {
    Vietnamese: [
      'Northern Vietnamese',
      'Central Vietnamese',
      'Southern Vietnamese',
    ],
  },
  {Turkish: ['Standard Turkish', 'Cypriot Turkish']},
  {Thai: ['Central Thai', 'Northern Thai', 'Southern Thai']},
  {Japanese: ['Standard Japanese', 'Kansai', 'Okinawan']},
  {Indonesian: ['Standard Indonesian', 'Jakarta Indonesian']},
  {Armenian: ['Eastern Armenian', 'Western Armenian']},
  {
    Persian: [
      'Iranian Persian (Farsi)',
      'Dari (Afghan Persian)',
      'Tajik Persian',
    ],
  },
  {Tagalog: ['Manila Tagalog', 'Cebuano Tagalog']},
  {Norwegian: ['Bokmål', 'Nynorsk']},
  {Swahili: ['Tanzanian Swahili', 'Kenyan Swahili', 'Congo Swahili']},
  {Hebrew: ['Modern Hebrew', 'Sephardic Hebrew']},
  {
    Yiddish: [
      'Eastern Yiddish (Litvish)',
      'Central Yiddish (Poylish)',
      'Western Yiddish',
    ],
  },
  'Polish',
  'Dutch',
  'Swedish',
  'Romanian',
  'Czech',
  'Hungarian',
  'Finnish',
  'Ukrainian',
  'Cape Verdean Creole',
  'Haitian Creole',
  'Danish',
  'Greek',
  'Bulgarian',
  'Slovak',
  'Lithuanian',
  'Catalan',
  'Slovenian',
  'Estonian',
  'Latvian',
  'Albanian',
  'Malay',
  'Azerbaijani',
  'Serbian',
  'Tamil',
  'Croatian',
  'Kazakh',
  'Malayalam',
  'Marathi',
  'Telugu',
  'Afrikaans',
  'Galician',
  'Belarusian',
  'Macedonian',
  'Basque',
  'Bosnian',
  'Uzbek',
  'Urdu',
  'Nepali',
  'Kannada',
  'Gujarati',
  'Kyrgyz',
  'Tajik',
  'Burmese',
  'Tatar',
  {Somali: ['Standard Somali', 'Maay Somali']},
  'Amharic',
  'Zulu',
  'Odia',
  'Luganda',
  'Kurdish (Sorani)',
  'Kabyle',
];

// Flatten TELEPHONIC_SUPPORTED_LANGUAGES to top-level language names. Entries
// are either a plain name or a `{Language: dialects[]}` map; the document
// translator surfaces these as base target languages and exposes each one's
// dialects (see dialectsForLanguage) as a secondary choice.
export const telephonicLanguageNames = (): string[] =>
  TELEPHONIC_SUPPORTED_LANGUAGES.map(item =>
    typeof item === 'object' ? Object.keys(item)[0] : item,
  );
